import React, { useEffect, useMemo } from "react";

import { RoutingLink } from "#batteries-included-components/RoutingLink";
import { useSearchParams } from "#routers/hooks";
import { linkToTestDetail } from "#src/Routers/links";
import {
  DataTable,
  DateRangeSelector,
  HeaderType,
  MultiDropdownInputWithSearch,
  Pill,
  Tooltip,
} from "@validereinc/common-components";
import moment from "moment";
import { SAMPLE_TYPES } from "../../utils/enums";
import { getStatusType } from "../Common/Table/rendererHelper";
import MeasurementsCell from "./MeasurementsCell";

const DEFAULT_MONTHLY_DATE_RANGE = 1;

const DEFAULT_DATE_RANGE = {
  from: moment().subtract(DEFAULT_MONTHLY_DATE_RANGE, "month").toDate(),
  to: moment().toDate(),
};

export default ({
  streams,
  sites,
  instruments,
  loading,
  getFormattedMeasurementValueWithUnit,
  supportedMeasurementTypes,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!searchParams?.from || !searchParams?.to) {
      setSearchParams({
        ...searchParams,
        from: searchParams.from || DEFAULT_DATE_RANGE.from,
        to: searchParams.to || DEFAULT_DATE_RANGE.to,
      });
    }
  }, [searchParams]);
  // name in filters should match with the subscription
  const filters = useMemo(
    () => [
      {
        name: "date",
        // @ts-expect-error legacy opshub typing
        component: <DateRangeSelector allowOneDayRange />,
        value:
          searchParams?.from && searchParams?.to
            ? {
                from: new Date(searchParams.from),
                to: new Date(searchParams.to),
              }
            : DEFAULT_DATE_RANGE,
      },
      {
        name: "site",
        component: (
          // @ts-expect-error legacy opshub typing
          <MultiDropdownInputWithSearch
            label={"Site"}
            labelKey={"name"}
            dropdownKey={"name"}
            options={sites}
            selectLimit={10}
            isClearable
          />
        ),
      },
      {
        name: "instrumentName",
        component: (
          // @ts-expect-error legacy opshub typing
          <MultiDropdownInputWithSearch
            label={"Instrument Name"}
            labelKey={"name"}
            dropdownKey={"name"}
            options={instruments}
            selectLimit={10}
            isClearable
          />
        ),
      },
      {
        name: "measurements",
        component: (
          // @ts-expect-error legacy opshub typing
          <MultiDropdownInputWithSearch
            label={"Measurements"}
            options={Object.values(supportedMeasurementTypes)}
            selectLimit={10}
            isClearable
          />
        ),
      },
      {
        name: "stream",
        component: (
          // @ts-expect-error legacy opshub typing
          <MultiDropdownInputWithSearch
            label={"Stream"}
            labelKey={"name"}
            dropdownKey={"name"}
            options={streams}
            selectLimit={10}
            isClearable
          />
        ),
      },
      {
        name: "samplePoint",
        component: (
          // @ts-expect-error legacy opshub typing
          <MultiDropdownInputWithSearch
            label={"Sample Point ID"}
            labelKey={"sample_point_ids"}
            dropdownKey={"sample_point_ids"}
            options={streams.map((stream) => {
              return {
                streamId: stream.id,
                sample_point_ids: stream.sample_point_ids,
              };
            })}
            selectLimit={10}
            isClearable
          />
        ),
      },
      {
        name: "sampleType",
        component: (
          // @ts-expect-error legacy opshub typing
          <MultiDropdownInputWithSearch
            label={"Sample Type"}
            options={Object.values(SAMPLE_TYPES)}
            selectLimit={4}
            isClearable
          />
        ),
      },
    ],
    [streams, sites]
  );

  const quickFilters = useMemo(() => {
    return {
      loading,
      items: [
        { label: "All" },
        {
          label: "Orphan Tests",
          variant: "error",
          value: "orphan",
        },
      ],
    };
  }, [loading]);

  const headers: Array<HeaderType<any>> = useMemo(() => {
    return [
      {
        label: "Test Name",
        key: "name",
        isFixed: true,
        renderComponent: ({ item }) => (
          <RoutingLink to={linkToTestDetail(item.id)}>{item.name}</RoutingLink>
        ),
      },
      {
        label: "Test Date",
        key: "date",
        variant: "date",
        isSortable: true,
        renderComponent: ({ item }) => (
          <DataTable.DataRow.DateCell
            value={item.date}
            withTime
          />
        ),
      },
      {
        label: "Tester",
        key: "tested_by",
        suppress: true,
      },
      {
        label: "Site",
        key: "instrument.site_name",
      },
      {
        label: "Status",
        key: "status",
        variant: "pill",
        renderComponent: ({ item }) => (
          <Pill variant={getStatusType(item.status, "test").type}>
            {item.status}
          </Pill>
        ),
      },
      {
        label: "Instrument Name",
        key: "instrument.name",
      },
      {
        label: "Stream",
        key: "sample.streamName",
      },
      {
        label: "Sample Point ID",
        key: "sample.samplePointIds",
      },
      {
        label: "Sample Date",
        key: "sample.started_at",
        renderComponent: ({ item }) => (
          <DataTable.DataRow.DateCell value={item.sample?.started_at} />
        ),
      },
      {
        label: "Sample Type",
        key: "sample.type",
      },
      {
        label: "Short Code",
        key: "short_code",
        renderComponent: ({ item }) => (
          <Pill variant={"default"}>{item.short_code}</Pill>
        ),
      },
      {
        label: "Measurements",
        key: "measurements",
        renderComponent: ({ item }) => {
          let measurements;
          if (item.measurements) {
            measurements = Object.entries(item.measurements).map(
              ([name, measurement]) => {
                const formattedValue = getFormattedMeasurementValueWithUnit(
                  name,
                  measurement
                );

                return { name, value: formattedValue };
              }
            );
          }

          return (
            <Tooltip
              title="Measurements"
              // @ts-expect-error legacy opshub typing
              content={<MeasurementsCell measurements={measurements} />}
            >
              <Pill variant="info">{measurements?.length}</Pill>
            </Tooltip>
          );
        },
      },
      {
        label: "Test Type",
        key: "type",
        isSortable: true,
        isFixed: true,
      },
    ];
  }, [sites, streams]);

  return { headers, filters, quickFilters };
};
